import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { FormUtils } from 'libs/shared/utils/helpers/src/lib/form-utils'
import { Observable, combineLatest, distinctUntilChanged, filter, map, of, take } from 'rxjs'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { StringMap } from 'libs/shared/models/shared.models'
import { BaseEventTicketsFacade } from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.facade'
import * as BaseEventTicketsActions from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.actions'
import { GbEventTicketFormQuestionsService } from 'libs/shared/event-tickets/utils/src/lib/gb-event-ticket-form-questions.service'
import {
  AssignEventTicketPayload,
  BaseEventTicketEntity,
} from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.models'
import { GbBaseDialogService } from 'libs/shared/utils/dialogs/src/lib/gb-base-dialog-service/gb-base-dialog.service'
import { GbEventTicketConfirmDialogComponent } from '../gb-event-ticket-confirm-dialog/gb-event-ticket-confirm-dialog.component'
import { SharedEventTicketPurchasesFacade } from 'libs/shared/models/event-tickets/event-ticket-purchases.models'
import { GbCookieService } from 'libs/shared/utils/services/src/lib/gb-cookie/gb-cookie.service'

@UntilDestroy()
@Component({
  selector: 'gb-event-ticket-multi-assign',
  templateUrl: './gb-event-ticket-multi-assign.component.html',
  styleUrls: ['./gb-event-ticket-multi-assign.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GbEventTicketMultiAssignComponent implements OnInit, OnDestroy {
  selectedPurchaseEventTickets$ = this.eventTicketPurchasesFacade.selectedPurchaseEventTickets$
  selectedPurchaseCustomFields$ = this.eventTicketPurchasesFacade.selectedPurchaseCustomFields$
  selectedPurchaseId$ = this.eventTicketPurchasesFacade.selectedPurchaseId$
  isPaymentPending$ = this.selectedPurchaseEventTickets$.pipe(map((eventTickets) => eventTickets.length === 0))

  guestForms: StringMap<FormGroup> = {}
  questionsForms: StringMap<FormGroup> = {}
  isSelfAssigned: StringMap<boolean | undefined> = {}
  isExpanded: StringMap<boolean | undefined> = {}
  allFormsValid = true

  get isAdminOrVolunteer(): boolean {
    return this.gbCookieService.isAdminOrVolunteer()
  }

  constructor(
    @Inject('EventTicketPurchasesFacade') private eventTicketPurchasesFacade: SharedEventTicketPurchasesFacade,
    private baseEventTicketsFacade: BaseEventTicketsFacade,
    private gbEventTicketFormQuestionsService: GbEventTicketFormQuestionsService,
    private gbDialogService: GbBaseDialogService,
    private notificationService: GbNotificationService,
    private gbCookieService: GbCookieService,
  ) {}

  ngOnInit(): void {
    this.handleSingleTicketExpand()

    // TODO: below is a workaround until GB-7242
    // disable body scroll when dialog is open
    document.body.classList.add('cdk-global-scrollblock')
  }

  ngOnDestroy(): void {
    // TODO: below is a workaround until GB-7242
    // re-enable body scroll when dialog is closed
    document.body.classList.remove('cdk-global-scrollblock')
  }

  syncEventTicketPurchase(uuid: string): void {
    this.eventTicketPurchasesFacade.dispatch(BaseEventTicketsActions.syncEventTicketPurchase({ uuid }))
  }

  handleSingleTicketExpand(): void {
    combineLatest([this.selectedPurchaseEventTickets$, this.selectedPurchaseCustomFields$])
      .pipe(
        distinctUntilChanged(),
        filter((eventTickets) => eventTickets.length > 0),
        untilDestroyed(this),
      )
      .subscribe(([eventTickets, customFields]) => {
        // expand panel if only one ticket and has custom fields
        if (eventTickets.length === 1 && customFields.length) {
          this.isExpanded[eventTickets[0].uuid] = true
        }
      })
  }

  onSelfAssignChange(isSelfAssigned: boolean, eventTicket: BaseEventTicketEntity): void {
    this.isSelfAssigned[eventTicket.uuid] = isSelfAssigned
  }

  getIconColor(eventTicket: BaseEventTicketEntity): string {
    return !!this.getGuestName(eventTicket) ? 'primary' : ''
  }

  getGuestName(eventTicket: BaseEventTicketEntity): string {
    const form = this.guestForms[eventTicket.uuid]
    const firstName = form?.get('first_name')?.value
    const lastName = form?.get('last_name')?.value

    return firstName || lastName ? `- ${firstName} ${lastName}`.trim() : ''
  }

  shouldShowQuestionsForm(eventTicket: BaseEventTicketEntity): boolean {
    if (!eventTicket.ticket_package.custom_fields.length) return false
    return !FormUtils.isFormEmpty(this.guestForms[eventTicket.uuid])
  }

  validateForms(
    forms: { [key: string]: FormGroup },
    condition: (uuid: string) => boolean | undefined,
    onInvalidFn: (form: FormGroup) => void,
  ): void {
    Object.entries(forms).forEach(([uuid, form]) => {
      if (condition(uuid) && !form.valid) {
        this.allFormsValid = false
        this.isExpanded[uuid] = true
        onInvalidFn(form)
      }
    })
  }

  validateAllForms(): void {
    this.allFormsValid = true // reset to true before validation

    this.validateForms(
      this.guestForms,
      (uuid: string) => !this.guestForms[uuid].disabled,
      (form) => form.markAllAsTouched(),
    )

    this.validateForms(
      this.questionsForms,
      (uuid: string) => this.isSelfAssigned[uuid],
      (form) => FormUtils.markFieldsAsTouched(form),
    )
  }

  scrollToFirstInvalidField(): void {
    const firstInvalidField = document.querySelector(
      'gb-event-ticket-guest-form .ng-invalid, gb-event-ticket-questions-form .gb-invalid',
    )
    firstInvalidField?.scrollIntoView({ behavior: 'smooth' })
  }

  onSubmit(eventTickets: BaseEventTicketEntity[]): void {
    this.validateAllForms()

    if (!this.allFormsValid) {
      this.notificationService.error('SHARED.ERROR.FORM_INVALID')
      setTimeout(() => this.scrollToFirstInvalidField())
      return
    }

    const { payload, unassignedTicketsCount } = this.createPayload(eventTickets)

    // if unassigned tickets, show confirmation dialog otherwise dispatch assign action.
    const shouldDispatch = unassignedTicketsCount ? this.confirmUnassignedTickets() : of(true)

    shouldDispatch.subscribe((result) => {
      if (!result) return

      // only dispatch if there are tickets to be assigned
      if (unassignedTicketsCount !== eventTickets.length) {
        this.baseEventTicketsFacade.dispatch(BaseEventTicketsActions.bulkAssignEventTickets({ payload }))
      } else {
        this.eventTicketPurchasesFacade.dispatch(BaseEventTicketsActions.finaliseEventTicketPurchaseSuccess())
      }
    })
  }

  createPayload(eventTickets: BaseEventTicketEntity[]) {
    let unassignedTicketsCount = 0

    const payload = eventTickets.reduce((acc: AssignEventTicketPayload[], eventTicket) => {
      if (FormUtils.isFormEmpty(this.guestForms[eventTicket.uuid])) {
        unassignedTicketsCount++
      } else {
        acc.push(this.createPayloadItem(eventTicket))
      }
      return acc
    }, [])

    return { payload, unassignedTicketsCount }
  }

  createPayloadItem(eventTicket: BaseEventTicketEntity): AssignEventTicketPayload {
    return {
      ...this.guestForms[eventTicket.uuid].getRawValue(),
      self_assign: !!this.isSelfAssigned[eventTicket.uuid],
      custom_values: this.questionsForms[eventTicket.uuid]
        ? this.gbEventTicketFormQuestionsService.getFormattedCustomValues(this.questionsForms[eventTicket.uuid].value)
        : undefined,
      instance: eventTicket.uuid,
    }
  }

  onSkip(): void {
    this.confirmUnassignedTickets().subscribe((result) => {
      if (result) {
        this.eventTicketPurchasesFacade.dispatch(BaseEventTicketsActions.finaliseEventTicketPurchaseSuccess())
      }
    })
  }

  confirmUnassignedTickets(): Observable<boolean> {
    return this.gbDialogService
      .getDialogObs(GbEventTicketConfirmDialogComponent, {
        data: {
          header: 'SHARED.EVENT_TICKETS.CONFIRM.UNASSIGNED_TICKETS_HEADER',
          description: this.isAdminOrVolunteer
            ? 'SHARED.EVENT_TICKETS.CONFIRM.UNASSIGNED_TICKETS_DESCRIPTION_ADMIN'
            : 'SHARED.EVENT_TICKETS.CONFIRM.UNASSIGNED_TICKETS_DESCRIPTION',
          confirmButtonText: 'SHARED.EVENT_TICKETS.CONFIRM.UNASSIGNED_TICKETS_CONFIRM_BUTTON',
          cancelButtonText: 'SHARED.EVENT_TICKETS.CONFIRM.UNASSIGN_TICKETS_CANCEL_BUTTON',
        },
        maxWidth: '500px',
      })
      .pipe(
        map((result) => !!result),
        take(1),
      )
  }
}
